import React, {
  forwardRef,
  useCallback, useContext, useRef, useState,
} from 'react';
import {
  Alert, AlertTitle, Box, Button, Dialog, Grid, Slide, Toolbar,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';
import api from '../../../api/req';
import { AppContext } from '../../../providers/authProvider';
import Loader from '../../../components/MaterialUI/loader';
import { EditorControls } from '../../BaseEditor';
import { checkTask } from '../../../api/checktask';

const xmlUrl = '/api/service/land_to_xml/';
const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

function LandSessionXml() {
  const [loading, setLoading] = useState(false);
  const htmlRef = useRef();
  const [html, setHtml] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState({ });
  const { auth, currentUser } = useContext(AppContext);

  const onDone = useCallback((result, resultType) => {
    setLoading(false);

    //
    if (resultType === 2) {
      const dd = /^data:application\/xml;base64,(.*)$/gm.exec(result.result.file);
      // eslint-disable-next-line no-debugger
      const xmlFile = decodeURIComponent(escape(atob(dd[1])));
      const blob = new Blob([xmlFile], { type: 'xml' });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.download = 'session.xml';
      a.href = url;

      document.body.append(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else {
      const dd = /^data:text\/html;base64,(.*)$/gm.exec(result.result.file);
      // eslint-disable-next-line no-debugger
      htmlRef.current.innerHTML = decodeURIComponent(escape(atob(dd[1])));

      if (htmlRef.current.innerHTML !== null) {
        setHtml(true);
      }
    }
  }, []);

  const getHtmlXmlFile = useCallback(
    (resultType) => {
      const req = async () => {
        const r = await api.post(xmlUrl, auth, {
          ...filter,
          result_type: resultType,
        });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        // eslint-disable-next-line camelcase
        const { task_id } = await r.json();
        // eslint-disable-next-line camelcase
        return task_id;
      };
      setLoading(true);
      setErr(null);
      req()
        .then((d) => checkTask(
          d,
          auth,
          (res) => {
            // eslint-disable-next-line no-unused-expressions
            res.result.errors ? setErr(res.result.errors[0]) : onDone(res.result, resultType);
            // eslint-disable-next-line no-unused-expressions
            res.status !== 'PENDING' && setLoading(false);
          },
          (e) => {
            setErr(e ? e.message : 'Скасовано');
            setLoading(false);
          },
        ))
        .catch((e) => {
          setErr(e.message);
          setLoading(false);
        });
    },
    [auth, filter, onDone],
  );

  const printAllowed = !!html;

  return (
    <>
      {loading && (
        <Loader text="Завантаження" />
      )}

      {err && (
        <Alert severity="error">
          <AlertTitle>:-(</AlertTitle>
          {err}
        </Alert>
      )}
      <Toolbar sx={{ gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<FontAwesomeIcon icon={faPrint} />}
          color="success"
          onClick={() => {
            setHtml(true);
            getHtmlXmlFile(1);
          }}
        >
          Переглянути
        </Button>

        <Button
          variant="contained"
          startIcon={<FontAwesomeIcon icon={faDownload} />}
          color="success"
          onClick={() => {
            getHtmlXmlFile(2);
          }}
        >
          XML
        </Button>
      </Toolbar>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          {currentUser.is_superuser && (
            <EditorControls.ItemPicker
              label="Установа"
              backend="/api/catalog/orgs/"
              canErase
              value={filter.org}
              onChange={(e, v) => setFilter(
                {
                  ...filter,
                  org: v,
                },
              )}
            />

          )}
        </Grid>
        <Grid item sm={6}>
          <EditorControls.DateInput
            value={filter.date}
            label="Дата"
            onChange={(e, v) => setFilter(
              {
                ...filter,
                date: v,
              },
            )}
          />
        </Grid>
        <Grid item sm={6}>
          <EditorControls.ItemPicker
            value={filter.session_decision}
            label="Рішення сесії"
            backend="/api/realty/sessiondecisions/"
            canErase
            onChange={(e, v) => setFilter(
              {
                ...filter,
                session_decision: v,
                session_decision_id: v.id,
              },
            )}
          />
        </Grid>
        <Grid item sm={6}>
          <EditorControls.ItemPicker
            value={filter.locality}
            label="Населений пункт"
            backend="/api/catalog/localities/"
            canErase
            onChange={(e, v) => setFilter(
              {
                ...filter,
                locality: v,
                locality_id: v.id,
              },
            )}
          />
        </Grid>
        <Grid item sm={6}>
          <EditorControls.ItemPicker
            value={filter.region}
            label="Регіональна ознака (старостинство)"
            backend="/api/catalog/regions/"
            canErase
            onChange={(e, v) => setFilter(
              {
                ...filter,
                region: v,
                region_id: v.id,
              },
            )}
          />
        </Grid>
      </Grid>
      {html && (
        <Dialog
          onClose={() => {
            htmlRef.current.innerHTML = '';
            setHtml(false);
          }}
          open={!!html}
          fullScreen
          TransitionComponent={Transition}
        >
            {loading && (
            <Loader text="Завантаження" />
            )}

          {err && (
          <Alert severity="error">
            <AlertTitle>:-(</AlertTitle>
            {err}
          </Alert>
          )}
          <Toolbar sx={{ gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faPrint} />}
              color="success"
              disabled={!printAllowed}
              onClick={() => {
                const pri = document.getElementById('print-area_land').contentWindow;
                pri.document.open();
                pri.document.write(htmlRef.current.innerHTML);
                pri.document.close();
                pri.focus();
                pri.print();
              }}
            >
              Друк
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                htmlRef.current.innerHTML = '';
                setHtml(false);
              }}
            >
              Закрити
            </Button>
          </Toolbar>
          <div
            id="print-area_xml"
          />
          <Box
            ref={htmlRef}
            margin="0 15px"
          />

            {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
          <iframe id="print-area_land" style={{ heigh: '0px', width: '0px', position: 'absolute' }} />
        </Dialog>
      )}
    </>
  );
}

export default LandSessionXml;
